/**
 * The organisation types sv_organization accepts (its `organization_type` enum, the
 * same set `samvision_admin` filters and sorts the platform's company list by).
 *
 * This exists because until now the cabinet named exactly one of them: company
 * creation hard-coded `'other'`, and the settings screen printed the value read-only.
 * A company created from the cabinet was therefore «Прочее» for ever, and the operator
 * console's filter by sector saw every one of them in the same bucket — a field that
 * silently means nothing is worse than a field nobody filled in, because the admin's
 * list looks correct.
 *
 * Labels resolve through i18n (`orgType.<value>`), never baked in here. An unknown
 * value coming off the wire (a sector added on the server) falls back to `other`
 * rather than rendering a machine token to a user.
 */
export const ORGANIZATION_TYPES = [
  'government',
  'school',
  'preschool',
  'higher_edu',
  'healthcare',
  'business',
  'industrial',
  'residential',
  'other',
] as const;

export type OrganizationType = (typeof ORGANIZATION_TYPES)[number];

/**
 * What a company gets when nobody chose. `other` and not `business`: guessing the
 * sector wrong is a worse default than admitting it is unset, since the value drives
 * how the platform's own operators segment their customers.
 */
export const DEFAULT_ORGANIZATION_TYPE: OrganizationType = 'other';

export function organizationTypeLabelKey(value: string | null | undefined): string {
  return (ORGANIZATION_TYPES as readonly string[]).includes(value ?? '')
    ? `orgType.${value}`
    : `orgType.${DEFAULT_ORGANIZATION_TYPE}`;
}

/** Narrow a wire value to something the picker can show as selected. */
export function asOrganizationType(value: string | null | undefined): OrganizationType {
  return (ORGANIZATION_TYPES as readonly string[]).includes(value ?? '')
    ? (value as OrganizationType)
    : DEFAULT_ORGANIZATION_TYPE;
}
